import React, { useState, useEffect } from 'react';
import AgeCalculator from './apps/AgeCalculator';
import CryptoTracker from './apps/CryptoTracker';
import ReflexTester from './apps/ReflexTester';
import WeatherReport from './apps/WeatherReport';
import RockPaperScissors from './apps/RockPaperScissors';
import PrimeFactors from './apps/PrimeFactors';
import AsciiClock from './apps/AsciiClock';
import GiphySearch from './apps/GiphySearch';
import Stopwatch from './apps/Stopwatch';
import CreditCardValidator from './apps/CreditCardValidator';
import IpChecker from './apps/IpChecker';
import { Application } from '../types';

const Apps: React.FC = (): JSX.Element => {
  const [selectedApp, setSelectedApp] = useState<Application | null>(null);

  const applicationList: Application[] = [
    {
      name: 'daysOfAge',
      description: 'How many days/min/seconds old are you?',
      component: AgeCalculator
    }, {
      name: 'cryptoTracker',
      description: 'Bitcoin Price Tracker',
      component: CryptoTracker
    }, {
      name: 'reflexTester',
      description: 'Reflex Tester',
      component: ReflexTester
    }, {
      name: 'weatherReport',
      description: 'Weather Reporter',
      component: WeatherReport
    }, {
      name: 'rockPaperScissors',
      description: 'Rock.Paper.Scissors',
      component: RockPaperScissors
    }, {
      name: 'primeFactors',
      description: 'Prime Factors',
      component: PrimeFactors
    }, {
      name: 'asciiClock',
      description: 'ASCII Digital Clock',
      component: AsciiClock
    }, {
      name: 'giphy',
      description: 'Giphy',
      component: GiphySearch
    }, {
      name: 'stopWatch',
      description: 'Stop Watch',
      component: Stopwatch
    }, {
      name: 'creditCard',
      description: 'Credit Card Validator',
      component: CreditCardValidator
    }, {
      name: 'ip',
      description: 'What is my IP Address?',
      component: IpChecker
    }
  ];

  const openApp = (app: Application): void => {
    setSelectedApp(app);
  };

  const closeApp = (): void => {
    setSelectedApp(null);
  };

  const getAppEmoji = (appName: string): string => {
    const emojiMap: { [key: string]: string } = {
      'daysOfAge': '🎂',
      'bitcoin': '₿',
      'reflex': '⚡',
      'weather': '🌤️',
      'rockPaperScissors': '✂️',
      'primeFactors': '🔢',
      'asciiClock': '🕐',
      'giphy': '🎭',
      'stopwatch': '⏱️',
      'creditCardValidator': '💳',
      'ipChecker': '🌐'
    };
    return emojiMap[appName] || '📱';
  };

  // Add keyboard support for closing modal
  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent): void => {
      if (event.key === 'Escape' && selectedApp) {
        closeApp();
      }
    };

    if (selectedApp) {
      document.addEventListener('keydown', handleKeyDown);
      // Prevent body scroll when modal is open
      document.body.style.overflow = 'hidden';
    }

    return () => {
      document.removeEventListener('keydown', handleKeyDown);
      document.body.style.overflow = 'unset';
    };
  }, [selectedApp]);

  const SelectedAppComponent = selectedApp ? selectedApp.component : null;

  return (
    <div className="container-fluid">
      <div className="row">
        <div className="col-12">
          <div className="apps-grid">
                {applicationList.map((application, index) => (
                  <div 
                    key={index}
                    className="app-card"
                    onClick={() => openApp(application)}
                  >
                    <div className="app-icon">
                      <span className="app-emoji">
                        {getAppEmoji(application.name)}
                      </span>
                    </div>
                    <div className="app-content">
                      <p className="app-description">
                        {application.description}
                      </p>
                    </div>
                  </div>
                ))}
          </div>

          {selectedApp && (
            <div 
              className="modal show" 
              style={{ display: 'block' }}
              onClick={(e) => {
                if (e.target === e.currentTarget) {
                  closeApp();
                }
              }}
            >
              <div className="modal-dialog modal-lg">
                <div className="modal-content">
                  <div className="modal-header">
                    <h5 className="modal-title">{selectedApp.description}</h5>
                    <button 
                      type="button" 
                      className="btn-close" 
                      onClick={closeApp}
                      style={{ 
                        background: 'none', 
                        border: 'none', 
                        fontSize: '24px', 
                        cursor: 'pointer',
                        padding: '0',
                        width: '30px',
                        height: '30px',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center'
                      }}
                      title="Close"
                    >
                      ×
                    </button>
                  </div>
                  <div className="modal-body">
                    {SelectedAppComponent && <SelectedAppComponent onClose={closeApp} />}
                  </div>
                </div>
              </div>
            </div>
          )}
          {selectedApp && (
            <div 
              className="modal-backdrop show"
              onClick={closeApp}
              style={{ cursor: 'pointer' }}
            ></div>
          )}
        </div>
      </div>
    </div>
  );
};

export default Apps;
